/**
 * Admin Healthcare Moderation Dashboard
 * Comprehensive moderation screens for Healthcare & Care jobs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Filter,
  Eye,
  Building2,
  FileText,
  Users,
  Settings,
  History,
  BarChart3,
  Flag,
  ExternalLink,
  Home,
  Heart,
  DollarSign,
  MapPin,
  Loader2,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  Ban,
  HelpCircle,
  Briefcase,
  UserCheck,
  Scale,
  Clipboard,
  Zap,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Risk score colors
const getRiskColor = (score) => {
  if (score < 30) return 'bg-green-100 text-green-700 border-green-200';
  if (score < 60) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-red-100 text-red-700 border-red-200';
};

// Status badges
const STATUS_CONFIG = {
  pending: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Pending' },
  flagged: { color: 'bg-amber-100 text-amber-700', icon: Flag, label: 'Flagged' },
  approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
  needs_info: { color: 'bg-blue-100 text-blue-700', icon: HelpCircle, label: 'Needs Info' },
};

// Flag chip component
const FlagChip = ({ flag }) => {
  const flagConfig = {
    dbs_mentioned: { label: 'DBS', color: 'bg-blue-100 text-blue-700' },
    dbs_not_stated: { label: 'DBS?', color: 'bg-amber-100 text-amber-700' },
    vulnerable_adults: { label: 'Vuln Adults', color: 'bg-purple-100 text-purple-700' },
    vulnerable_children: { label: 'Children', color: 'bg-purple-100 text-purple-700' },
    home_visits: { label: 'Home Visit', color: 'bg-cyan-100 text-cyan-700' },
    live_in: { label: 'Live-in', color: 'bg-indigo-100 text-indigo-700' },
    urgent_hire: { label: 'Urgent', color: 'bg-red-100 text-red-700' },
    external_link: { label: 'External', color: 'bg-orange-100 text-orange-700' },
    pay_missing: { label: 'Pay?', color: 'bg-gray-100 text-gray-700' },
    cash_in_hand: { label: 'Cash', color: 'bg-red-100 text-red-700' },
    safeguarding_missing: { label: 'Safeguard?', color: 'bg-amber-100 text-amber-700' },
  };
  
  const config = flagConfig[flag] || { label: flag, color: 'bg-gray-100 text-gray-700' };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Moderation Queue Tab
const ModerationQueueTab = ({ country, category = 'all', onSelectJob }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    risk_band: 'all',
    role_type: '',
    search: '',
  });
  const [selectedJobs, setSelectedJobs] = useState([]);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ country });
      if (category && category !== 'all') params.append('category', category);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.risk_band && filters.risk_band !== 'all') params.append('risk_band', filters.risk_band);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/queue?${params}`);
      if (response.data.success) {
        setJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
    } finally {
      setLoading(false);
    }
  }, [country, category, filters]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleBulkAction = async (action) => {
    if (selectedJobs.length === 0) return;
    
    try {
      // Process each selected job
      for (const jobId of selectedJobs) {
        await axios.post(`${API_URL}/api/admin/healthcare-moderation/job/${jobId}/decision`, {
          decision: action,
          admin_id: 'admin',
        });
      }
      
      toast({
        title: 'Bulk action completed',
        description: `${selectedJobs.length} jobs ${action}ed`,
      });
      
      setSelectedJobs([]);
      fetchQueue();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process bulk action',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search job title or employer..."
                  className="pl-10"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
            </div>
            
            <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="flagged">Flagged</SelectItem>
                <SelectItem value="needs_info">Needs Info</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.risk_band} onValueChange={(v) => setFilters(prev => ({ ...prev, risk_band: v }))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk</SelectItem>
                <SelectItem value="low">Low (0-29)</SelectItem>
                <SelectItem value="medium">Medium (30-59)</SelectItem>
                <SelectItem value="high">High (60+)</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="sm" onClick={fetchQueue}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedJobs.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">{selectedJobs.length} selected</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('approve')}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('hold')}>
                  <Clock className="h-4 w-4 mr-1" /> Hold
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('reject')}>
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center p-8 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No jobs in moderation queue</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left">
                      <Checkbox
                        checked={selectedJobs.length === jobs.length}
                        onCheckedChange={(checked) => {
                          setSelectedJobs(checked ? jobs.map(j => j.id) : []);
                        }}
                      />
                    </th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Job / Employer</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Apply</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => onSelectJob(job.id)}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedJobs.includes(job.id)}
                          onCheckedChange={(checked) => {
                            setSelectedJobs(prev =>
                              checked ? [...prev, job.id] : prev.filter(id => id !== job.id)
                            );
                          }}
                        />
                      </td>
                      <td className="p-3 text-sm text-gray-500">
                        {new Date(job.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-xs">
                          <span className="mr-1">{job.category_icon || '💼'}</span>
                          {job.category_name || 'General'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-gray-900">{job.title}</div>
                        <div className="text-sm text-gray-500">{job.company_name}</div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">{job.location}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={job.apply_method === 'external' ? 'border-orange-300 text-orange-700' : ''}>
                          {job.apply_method === 'external' ? (
                            <><ExternalLink className="h-3 w-3 mr-1" /> External</>
                          ) : 'In-app'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {job.flags?.slice(0, 3).map((flag, i) => (
                            <FlagChip key={i} flag={flag} />
                          ))}
                          {job.flags?.length > 3 && (
                            <span className="text-xs text-gray-500">+{job.flags.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={`${getRiskColor(job.risk_score)} border`}>
                          {job.risk_score}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {(() => {
                          const config = STATUS_CONFIG[job.moderation_status] || STATUS_CONFIG.pending;
                          const Icon = config.icon;
                          return (
                            <Badge className={config.color}>
                              <Icon className="h-3 w-3 mr-1" />
                              {config.label}
                            </Badge>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Job Inspector Drawer
const JobInspectorDrawer = ({ jobId, open, onClose, onDecision }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [showRequestInfo, setShowRequestInfo] = useState(false);

  useEffect(() => {
    if (jobId && open) {
      fetchJobDetails();
    }
  }, [jobId, open]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/job/${jobId}/inspect`);
      if (response.data.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (decision) => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/healthcare-moderation/job/${jobId}/decision`, {
        decision,
        conditions,
        admin_id: 'admin',
      });
      
      if (response.data.success) {
        toast({
          title: 'Decision submitted',
          description: `Job ${decision}${decision === 'approve' ? 'd' : 'ed'} successfully`,
        });
        onDecision?.();
        onClose();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit decision',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-[600px] sm:max-w-[600px]">
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  const job = data?.job;
  const findings = data?.ai_findings;
  const compliance = data?.compliance_check;
  const employer = data?.employer_summary;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Listing Review</SheetTitle>
        </SheetHeader>

        {job && (
          <div className="space-y-6 mt-6">
            {/* Header */}
            <div>
              <h3 className="text-lg font-semibold">{job.title}</h3>
              <p className="text-gray-600">Employer: {job.company_name}</p>
              <div className="flex items-center gap-4 mt-2">
                <Badge variant="outline">{job.country}</Badge>
                <Badge className={`${getRiskColor(findings?.combined_score || 0)} border`}>
                  Risk: {findings?.combined_score}
                </Badge>
              </div>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleDecision('approve')}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDecision('hold')}>
                    <Clock className="h-4 w-4 mr-1" /> Hold
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowRequestInfo(true)}>
                    <HelpCircle className="h-4 w-4 mr-1" /> Request Info
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDecision('reject')}>
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                  <Button size="sm" variant="destructive" className="bg-red-800" onClick={() => handleDecision('suspend_employer')}>
                    <Ban className="h-4 w-4 mr-1" /> Suspend Employer
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Conditions */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Conditions (approve with limits)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { id: 'force_in_platform_apply', label: 'Force in-platform apply only' },
                  { id: 'disable_promotions', label: 'Disable promotions for this listing' },
                  { id: 'require_pay_range', label: 'Require pay range before publish' },
                  { id: 'require_employer_l2', label: 'Require Employer Verification ≥ L2' },
                ].map((condition) => (
                  <div key={condition.id} className="flex items-center gap-2">
                    <Checkbox
                      id={condition.id}
                      checked={conditions.includes(condition.id)}
                      onCheckedChange={(checked) => {
                        setConditions(prev =>
                          checked ? [...prev, condition.id] : prev.filter(c => c !== condition.id)
                        );
                      }}
                    />
                    <Label htmlFor={condition.id} className="text-sm">{condition.label}</Label>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Compliance Checks - Dynamic per Category */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clipboard className="h-4 w-4" />
                  {data?.category_rules?.name || 'Compliance'} Checks
                  <Badge variant="outline" className="text-xs">
                    {data?.category_rules?.backgroundCheckType || 'Generic'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Dynamic Compliance Checks from Category Rules */}
                {data?.category_rules?.complianceChecks?.length > 0 ? (
                  <div className="space-y-2">
                    {data.category_rules.complianceChecks.map((check, idx) => {
                      // Determine check status based on compliance data
                      let status = 'unknown';
                      let statusColor = 'text-gray-500';
                      
                      if (check.id === 'vulnerable_group' || check.id === 'working_with_children' || check.id === 'working_with_vulnerable') {
                        status = compliance?.vulnerable_group ? 'flagged' : 'clear';
                        statusColor = compliance?.vulnerable_group ? 'text-amber-600' : 'text-green-600';
                      } else if (check.id === 'home_visits') {
                        status = compliance?.home_visit_role ? 'flagged' : 'clear';
                        statusColor = compliance?.home_visit_role ? 'text-amber-600' : 'text-green-600';
                      } else if (check.id === 'live_in') {
                        status = compliance?.live_in_role ? 'flagged' : 'clear';
                        statusColor = compliance?.live_in_role ? 'text-amber-600' : 'text-green-600';
                      } else if (check.id === 'dbs_required' || check.id === 'dbs_guidance') {
                        status = compliance?.dbs_required === true ? 'present' : compliance?.dbs_required === false ? 'not_required' : 'missing';
                        statusColor = compliance?.dbs_required === null ? 'text-amber-600' : 'text-green-600';
                      } else if (check.id === 'safeguarding_statement' || check.id === 'safeguarding_language') {
                        status = compliance?.safeguarding_statement ? 'present' : 'missing';
                        statusColor = compliance?.safeguarding_statement ? 'text-green-600' : 'text-red-600';
                      } else if (check.id === 'licence_required') {
                        status = 'check_manually';
                        statusColor = 'text-blue-600';
                      }
                      
                      return (
                        <div key={idx} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2">
                            {check.required && (
                              <span className="text-red-500 text-xs">*</span>
                            )}
                            <span className="text-sm">{check.label}</span>
                            <Badge variant="outline" className="text-xs capitalize">{check.type}</Badge>
                          </div>
                          <span className={`text-sm font-medium capitalize ${statusColor}`}>
                            {status === 'unknown' ? '-' : status.replace('_', ' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Fallback to original static checks
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vulnerable group:</span>
                      <span className={compliance?.vulnerable_group ? 'text-amber-600 font-medium' : ''}>
                        {compliance?.vulnerable_group || 'None'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Home visits:</span>
                      <span className={compliance?.home_visit_role ? 'text-amber-600 font-medium' : ''}>
                        {compliance?.home_visit_role ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Live-in:</span>
                      <span className={compliance?.live_in_role ? 'text-amber-600 font-medium' : ''}>
                        {compliance?.live_in_role ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">DBS required:</span>
                      <span className={compliance?.dbs_required === null ? 'text-amber-600' : ''}>
                        {compliance?.dbs_required === true ? 'Yes' : compliance?.dbs_required === false ? 'No' : 'Not stated'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pay stated:</span>
                      <span className={!compliance?.pay_stated ? 'text-amber-600' : ''}>
                        {compliance?.pay_stated ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Safeguarding:</span>
                      <span className={!compliance?.safeguarding_statement ? 'text-red-600' : 'text-green-600'}>
                        {compliance?.safeguarding_statement ? 'Present' : 'Missing'}
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Category-specific flags */}
                {data?.category_rules?.flagTypes?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2">Category Flags to Watch:</p>
                    <div className="flex flex-wrap gap-1">
                      {data.category_rules.flagTypes.slice(0, 6).map((flag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs capitalize">
                          {flag.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Findings */}
            <Card className={findings?.combined_score >= 60 ? 'border-red-300 bg-red-50' : ''}>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  AI Findings
                  <Badge className={getRiskColor(findings?.combined_score || 0)}>
                    Combined: {findings?.combined_score}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Fraud Risk</span>
                      <div className={`font-bold ${findings?.fraud_score >= 60 ? 'text-red-600' : findings?.fraud_score >= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                        {findings?.fraud_score}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Compliance Risk</span>
                      <div className={`font-bold ${findings?.compliance_score >= 50 ? 'text-red-600' : findings?.compliance_score >= 25 ? 'text-amber-600' : 'text-green-600'}`}>
                        {findings?.compliance_score}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Confidence</span>
                      <div className="font-medium capitalize">{findings?.confidence}</div>
                    </div>
                  </div>
                  
                  {(findings?.fraud_reasons?.length > 0 || findings?.compliance_reasons?.length > 0) && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-500">Reasons:</p>
                      <ul className="text-sm space-y-1">
                        {findings?.fraud_reasons?.map((reason, i) => (
                          <li key={`f-${i}`} className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            {reason}
                          </li>
                        ))}
                        {findings?.compliance_reasons?.map((reason, i) => (
                          <li key={`c-${i}`} className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t">
                    <span className="text-xs text-gray-500">Suggested action: </span>
                    <Badge variant="outline" className="capitalize">{findings?.suggested_action}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Confidence Score & Improvement Suggestions */}
            {data?.improvement_suggestions && (
              <Card className={`border-2 ${
                data.improvement_suggestions.confidence_color === 'green' ? 'border-green-300 bg-green-50' :
                data.improvement_suggestions.confidence_color === 'amber' ? 'border-amber-300 bg-amber-50' :
                'border-red-300 bg-red-50'
              }`}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      AI Confidence Score
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-bold ${
                        data.improvement_suggestions.confidence_color === 'green' ? 'text-green-600' :
                        data.improvement_suggestions.confidence_color === 'amber' ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        {data.improvement_suggestions.confidence_score}%
                      </span>
                      <Badge className={
                        data.improvement_suggestions.confidence_color === 'green' ? 'bg-green-500' :
                        data.improvement_suggestions.confidence_color === 'amber' ? 'bg-amber-500' :
                        'bg-red-500'
                      }>
                        {data.improvement_suggestions.confidence_label}
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    {data.improvement_suggestions.confidence_message}
                  </p>
                  
                  {/* Required Fixes */}
                  {data.improvement_suggestions.required_fixes?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-red-600 uppercase flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Required Fixes ({data.improvement_suggestions.required_fixes.length})
                      </p>
                      <div className="space-y-2">
                        {data.improvement_suggestions.required_fixes.map((fix, idx) => (
                          <div key={idx} className="p-2 bg-red-100 rounded-lg border border-red-200">
                            <p className="text-sm font-medium text-red-800">{fix.title}</p>
                            <p className="text-xs text-red-600">{fix.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Quick Wins */}
                  {data.improvement_suggestions.quick_wins?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-amber-600 uppercase flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        Quick Wins ({data.improvement_suggestions.quick_wins.length})
                      </p>
                      <div className="space-y-2">
                        {data.improvement_suggestions.quick_wins.map((win, idx) => (
                          <div key={idx} className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-sm font-medium text-amber-800">{win.title}</p>
                            <p className="text-xs text-amber-600">{win.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* General Suggestions */}
                  {data.improvement_suggestions.suggestions?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-blue-600 uppercase flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Suggestions ({data.improvement_suggestions.suggestions.length})
                      </p>
                      <div className="space-y-1">
                        {data.improvement_suggestions.suggestions.map((sug, idx) => (
                          <div key={idx} className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm font-medium text-blue-800">{sug.title}</p>
                            <p className="text-xs text-blue-600">{sug.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Approval Likelihood */}
                  <div className="pt-2 border-t flex items-center justify-between">
                    <span className="text-xs text-gray-500">Approval Likelihood:</span>
                    <Badge className={
                      data.improvement_suggestions.approval_likelihood === 'likely' ? 'bg-green-500' :
                      data.improvement_suggestions.approval_likelihood === 'possible' ? 'bg-amber-500' :
                      'bg-red-500'
                    }>
                      {data.improvement_suggestions.approval_likelihood === 'likely' ? '✓ Likely to Approve' :
                       data.improvement_suggestions.approval_likelihood === 'possible' ? '? Needs Review' :
                       '✗ Unlikely'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employer Summary */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Employer Trust Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Verification:</span>
                    <Badge variant="outline">{employer?.verification_level || 'L0'}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Past rejections:</span>
                    <span className={employer?.past_rejections > 0 ? 'text-red-600 font-medium' : ''}>
                      {employer?.past_rejections || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reports (30d):</span>
                    <span className={employer?.reports_30d > 0 ? 'text-amber-600 font-medium' : ''}>
                      {employer?.reports_30d || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Jobs posted (30d):</span>
                    <span>{employer?.jobs_posted_30d || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Preview */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Job Preview (as user sees)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">{job.title}</h4>
                  <p className="text-sm text-gray-600">{job.company_name}</p>
                  <div className="flex gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-4 w-4" />
                      {job.salary_min ? `${job.currency_symbol}${job.salary_min.toLocaleString()}` : 'Competitive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {job.description?.slice(0, 300)}...
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Audit Log */}
            {data?.audit_log?.length > 0 && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Audit Log</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {data.audit_log.map((log, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-600">
                        <span className="text-gray-400">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                        <span className="font-medium capitalize">{log.action}</span>
                        {log.admin_id && <span>by {log.admin_id}</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Request Info Dialog */}
        <RequestInfoDialog
          open={showRequestInfo}
          onClose={() => setShowRequestInfo(false)}
          jobId={jobId}
          onSuccess={() => {
            setShowRequestInfo(false);
            onDecision?.();
            onClose();
          }}
        />
      </SheetContent>
    </Sheet>
  );
};

// Request Info Dialog
const RequestInfoDialog = ({ open, onClose, jobId, onSuccess }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([
    { id: 'pay_range', description: 'Add pay range or mark "Competitive"', required: true, checked: false },
    { id: 'dbs_clarify', description: 'Clarify DBS / background check policy', required: true, checked: false },
    { id: 'safeguarding', description: 'Add safeguarding statement', required: true, checked: false },
    { id: 'shift_pattern', description: 'Clarify shift pattern (nights/days)', required: false, checked: false },
    { id: 'provider_reg', description: 'Provide care provider registration info', required: false, checked: false },
    { id: 'remove_discriminatory', description: 'Remove discriminatory language', required: true, checked: false },
    { id: 'remove_guaranteed', description: 'Remove "guaranteed job" claims', required: true, checked: false },
  ]);
  const [deadline, setDeadline] = useState('72');
  const [autoPause, setAutoPause] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const selectedItems = items.filter(i => i.checked).map(i => ({
      item_id: i.id,
      description: i.description,
      required: i.required,
    }));

    if (selectedItems.length === 0) {
      toast({
        title: 'Select items',
        description: 'Please select at least one item to request',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/admin/healthcare-moderation/job/${jobId}/request-info`, {
        job_id: jobId,
        items: selectedItems,
        deadline_hours: parseInt(deadline),
        auto_pause: autoPause,
      });

      if (response.data.success) {
        toast({
          title: 'Request sent',
          description: 'Information request sent to employer',
        });
        onSuccess?.();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send request',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Info from Employer</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <Label>Select missing/unclear items:</Label>
            {items.map((item, i) => (
              <div key={item.id} className="flex items-start gap-2">
                <Checkbox
                  id={item.id}
                  checked={item.checked}
                  onCheckedChange={(checked) => {
                    const newItems = [...items];
                    newItems[i].checked = checked;
                    setItems(newItems);
                  }}
                />
                <Label htmlFor={item.id} className="text-sm leading-tight cursor-pointer">
                  {item.description}
                </Label>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label>Deadline</Label>
            <Select value={deadline} onValueChange={setDeadline}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24">24 hours</SelectItem>
                <SelectItem value="48">48 hours</SelectItem>
                <SelectItem value="72">72 hours</SelectItem>
                <SelectItem value="168">1 week</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="auto_pause"
              checked={autoPause}
              onCheckedChange={setAutoPause}
            />
            <Label htmlFor="auto_pause" className="text-sm">Auto-pause listing until resolved</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Compliance Rules Tab
const ComplianceRulesTab = ({ country }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    fetchRules();
  }, [country]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/rules/${country}`);
      if (response.data.success) {
        setRules(response.data.rules);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        enabled: rules.enabled,
        payRangeRequired: rules.jobPosting?.payRangeRequired,
        externalApplyAllowed: rules.jobPosting?.externalApplyAllowed,
        inPlatformApplyPreferred: rules.jobPosting?.inPlatformApplyPreferred,
        requireNoticeForVulnerableRoles: rules.safeguarding?.requireNoticeForVulnerableRoles,
        requireNoticeForHomeVisits: rules.safeguarding?.requireNoticeForHomeVisits,
        requireNoticeForLiveIn: rules.safeguarding?.requireNoticeForLiveIn,
        backgroundCheckType: rules.backgroundChecks?.type,
        mandatoryBackgroundForVulnerable: rules.backgroundChecks?.mandatoryForVulnerableRoles,
        minEmployerLevelToPost: rules.verificationGates?.minEmployerLevelToPost,
        minEmployerLevelForPromotions: rules.verificationGates?.minEmployerLevelForPromotions,
        minEmployerLevelForHighRiskRoles: rules.verificationGates?.minEmployerLevelForHighRiskRoles,
        autoHoldFraudScore: rules.moderation?.autoHoldFraudScore,
        autoHoldComplianceScore: rules.moderation?.autoHoldComplianceScore,
        autoRejectFraudScore: rules.moderation?.autoRejectFraudScore,
      };

      await axios.put(`${API_URL}/api/admin/healthcare-moderation/rules/${country}`, payload);

      toast({
        title: 'Rules saved',
        description: `Healthcare rules updated for ${country}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save rules',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Posting Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Posting Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Pay range required</Label>
            <Switch
              checked={rules?.jobPosting?.payRangeRequired}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                jobPosting: { ...prev.jobPosting, payRangeRequired: v }
              }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>In-platform apply preferred</Label>
            <Switch
              checked={rules?.jobPosting?.inPlatformApplyPreferred}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                jobPosting: { ...prev.jobPosting, inPlatformApplyPreferred: v }
              }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>External apply allowed</Label>
            <Switch
              checked={rules?.jobPosting?.externalApplyAllowed}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                jobPosting: { ...prev.jobPosting, externalApplyAllowed: v }
              }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Safeguarding */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Safeguarding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Notice required for vulnerable roles</Label>
            <Switch
              checked={rules?.safeguarding?.requireNoticeForVulnerableRoles}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                safeguarding: { ...prev.safeguarding, requireNoticeForVulnerableRoles: v }
              }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Notice required for home visits</Label>
            <Switch
              checked={rules?.safeguarding?.requireNoticeForHomeVisits}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                safeguarding: { ...prev.safeguarding, requireNoticeForHomeVisits: v }
              }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Notice required for live-in roles</Label>
            <Switch
              checked={rules?.safeguarding?.requireNoticeForLiveIn}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                safeguarding: { ...prev.safeguarding, requireNoticeForLiveIn: v }
              }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Background Checks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Background Checks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Check type</Label>
            <Select
              value={rules?.backgroundChecks?.type}
              onValueChange={(v) => setRules(prev => ({
                ...prev,
                backgroundChecks: { ...prev.backgroundChecks, type: v }
              }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DBS">DBS (UK)</SelectItem>
                <SelectItem value="POLICE_CLEARANCE">Police Clearance</SelectItem>
                <SelectItem value="BACKGROUND_CHECK_GENERIC">Generic Background Check</SelectItem>
                <SelectItem value="NONE">None</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label>Mandatory for vulnerable roles</Label>
            <Switch
              checked={rules?.backgroundChecks?.mandatoryForVulnerableRoles}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                backgroundChecks: { ...prev.backgroundChecks, mandatoryForVulnerableRoles: v }
              }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Verification Gates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Verification Gates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Min employer level to post</Label>
            <Select
              value={String(rules?.verificationGates?.minEmployerLevelToPost || 1)}
              onValueChange={(v) => setRules(prev => ({
                ...prev,
                verificationGates: { ...prev.verificationGates, minEmployerLevelToPost: parseInt(v) }
              }))}
            >
              <SelectTrigger className="mt-1 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map(l => <SelectItem key={l} value={String(l)}>L{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Min for promotions</Label>
            <Select
              value={String(rules?.verificationGates?.minEmployerLevelForPromotions || 2)}
              onValueChange={(v) => setRules(prev => ({
                ...prev,
                verificationGates: { ...prev.verificationGates, minEmployerLevelForPromotions: parseInt(v) }
              }))}
            >
              <SelectTrigger className="mt-1 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map(l => <SelectItem key={l} value={String(l)}>L{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Min for high-risk roles</Label>
            <Select
              value={String(rules?.verificationGates?.minEmployerLevelForHighRiskRoles || 2)}
              onValueChange={(v) => setRules(prev => ({
                ...prev,
                verificationGates: { ...prev.verificationGates, minEmployerLevelForHighRiskRoles: parseInt(v) }
              }))}
            >
              <SelectTrigger className="mt-1 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3].map(l => <SelectItem key={l} value={String(l)}>L{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Auto Moderation Thresholds */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Auto Moderation Thresholds</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Hold if Fraud score ≥</Label>
            <Input
              type="number"
              className="mt-1 w-24"
              value={rules?.moderation?.autoHoldFraudScore || 60}
              onChange={(e) => setRules(prev => ({
                ...prev,
                moderation: { ...prev.moderation, autoHoldFraudScore: parseInt(e.target.value) }
              }))}
            />
          </div>
          <div>
            <Label>Hold if Compliance score ≥</Label>
            <Input
              type="number"
              className="mt-1 w-24"
              value={rules?.moderation?.autoHoldComplianceScore || 50}
              onChange={(e) => setRules(prev => ({
                ...prev,
                moderation: { ...prev.moderation, autoHoldComplianceScore: parseInt(e.target.value) }
              }))}
            />
          </div>
          <div>
            <Label>Reject if Fraud score ≥</Label>
            <Input
              type="number"
              className="mt-1 w-24"
              value={rules?.moderation?.autoRejectFraudScore || 85}
              onChange={(e) => setRules(prev => ({
                ...prev,
                moderation: { ...prev.moderation, autoRejectFraudScore: parseInt(e.target.value) }
              }))}
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full">
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        Save Changes
      </Button>
    </div>
  );
};

// Main Component
const AdminHealthcareModeration = () => {
  const [country, setCountry] = useState('UK');
  const [activeTab, setActiveTab] = useState('queue');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all'); // NEW: Category filter
  const [categories, setCategories] = useState([]); // NEW: Available categories

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [country, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/categories`);
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/stats?country=${country}`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSelectJob = (jobId) => {
    setSelectedJobId(jobId);
    setDrawerOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-jobs-moderation">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Shield className="h-7 w-7" />
                Jobs Moderation
              </h1>
              <p className="text-indigo-100 mt-1">Admin → Jobs → Moderation</p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => window.location.href = '/admin/ai-accuracy'}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                AI Accuracy
              </Button>
              
              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px] bg-white/10 border-white/20 text-white">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.slug}>
                      <span className="flex items-center gap-2">
                        <span>{cat.icon}</span>
                        <span>{cat.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Country Filter */}
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                  <SelectItem value="SL">Sri Lanka</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-6 gap-4 mt-6">
              {[
                { label: 'Pending', value: stats.pending, color: 'bg-white/20' },
                { label: 'Flagged', value: stats.flagged, color: 'bg-amber-500/30' },
                { label: 'Needs Info', value: stats.needs_info, color: 'bg-blue-500/30' },
                { label: 'High Risk', value: stats.high_risk, color: 'bg-red-500/30' },
                { label: 'Approved', value: stats.approved, color: 'bg-green-500/30' },
                { label: 'Rejected', value: stats.rejected, color: 'bg-gray-500/30' },
              ].map((stat) => (
                <div key={stat.label} className={`${stat.color} rounded-lg p-3 text-center`}>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-sm text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="queue" className="flex items-center gap-2">
              <Clipboard className="h-4 w-4" />
              Moderation Queue
            </TabsTrigger>
            <TabsTrigger value="safeguarding" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Safeguarding Queue
            </TabsTrigger>
            <TabsTrigger value="employers" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Employer Verification
            </TabsTrigger>
            <TabsTrigger value="rules" className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Compliance Rules
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Moderation Settings
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="queue">
            <ModerationQueueTab country={country} category={selectedCategory} onSelectJob={handleSelectJob} />
          </TabsContent>

          <TabsContent value="safeguarding">
            <SafeguardingQueueTab country={country} onSelectJob={handleSelectJob} />
          </TabsContent>

          <TabsContent value="employers">
            <EmployerVerificationTab country={country} />
          </TabsContent>

          <TabsContent value="rules">
            <ComplianceRulesTab country={country} />
          </TabsContent>

          <TabsContent value="settings">
            <ModerationSettingsTab country={country} />
          </TabsContent>

          <TabsContent value="audit">
            <AuditLogTab country={country} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Job Inspector Drawer */}
      <JobInspectorDrawer
        jobId={selectedJobId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDecision={() => {
          fetchStats();
        }}
      />
    </div>
  );
};

// Safeguarding Queue Tab
const SafeguardingQueueTab = ({ country, onSelectJob }) => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchQueue();
  }, [country]);

  const fetchQueue = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/safeguarding-queue?country=${country}`);
      if (response.data.success) {
        setJobs(response.data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching safeguarding queue:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-semibold">High Risk Queue</span>
            <span className="text-sm">- Requires manual review</span>
          </div>
        </CardContent>
      </Card>

      {jobs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Shield className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p>No high-risk jobs in queue</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <Card
              key={job.id}
              className="border-red-200 hover:border-red-400 cursor-pointer transition-colors"
              onClick={() => onSelectJob(job.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{job.title}</h4>
                    <p className="text-sm text-gray-600">{job.company_name} • {job.location}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {job.flags?.map((flag, i) => (
                        <FlagChip key={i} flag={flag} />
                      ))}
                    </div>
                  </div>
                  <Badge className="bg-red-100 text-red-700 border border-red-200">
                    Risk: {job.risk_score}
                  </Badge>
                </div>
                <div className="mt-3 pt-3 border-t text-sm text-gray-500">
                  <div className="flex gap-4">
                    {job.risk_factors?.vulnerable_group && (
                      <span className="text-purple-600">• Vulnerable: {job.risk_factors.vulnerable_group}</span>
                    )}
                    {job.risk_factors?.home_visits && (
                      <span className="text-cyan-600">• Home visits</span>
                    )}
                    {job.risk_factors?.live_in && (
                      <span className="text-indigo-600">• Live-in</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Employer Verification Tab
const EmployerVerificationTab = ({ country }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);

  useEffect(() => {
    fetchEmployers();
  }, [country]);

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/employers?country=${country}`);
      if (response.data.success) {
        setEmployers(response.data.employers || []);
      }
    } catch (error) {
      console.error('Error fetching employers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVerification = async (employerId, level) => {
    try {
      await axios.put(`${API_URL}/api/admin/healthcare-moderation/employer/${employerId}/verification?verification_level=${level}`);
      toast({
        title: 'Verification updated',
        description: `Employer verification set to ${level}`,
      });
      fetchEmployers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update verification',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {employers.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No employers to verify</p>
          </CardContent>
        </Card>
      ) : (
        employers.map((employer) => (
          <Card key={employer.user_id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold">{employer.company_name}</h4>
                  <p className="text-sm text-gray-600">{employer.email}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <Badge variant="outline">{employer.verification_level || 'L0'}</Badge>
                    <span className="text-gray-500">Provider: {employer.provider_type || 'Unknown'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {['L0', 'L1', 'L2', 'L3'].map((level) => (
                    <Button
                      key={level}
                      size="sm"
                      variant={employer.verification_level === level ? 'default' : 'outline'}
                      onClick={() => handleUpdateVerification(employer.user_id, level)}
                    >
                      {level}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

// Moderation Settings Tab - AI Auto-Approval, Country Controls
const ModerationSettingsTab = ({ country }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    ai_auto_approval_enabled: false,
    ai_auto_approval_threshold: 20,
    ai_auto_reject_enabled: false,
    ai_auto_reject_threshold: 85,
    compliance_checks_enabled: true,
    email_notifications_enabled: true,
  });
  const [categoryRules, setCategoryRules] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('healthcare-care');
  const [runningBatch, setRunningBatch] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchCategoryRules();
  }, [country]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/settings?country=${country}`);
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/all-categories?country=${country}`);
      if (response.data.success) {
        setCategoryRules(response.data.categories || {});
      }
    } catch (error) {
      console.error('Error fetching category rules:', error);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/admin/healthcare-moderation/settings?country=${country}`,
        settings
      );
      
      if (response.data.success) {
        toast({
          title: 'Settings Saved',
          description: `Moderation settings updated for ${country}`,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const runBatchModeration = async () => {
    try {
      setRunningBatch(true);
      const response = await axios.post(
        `${API_URL}/api/admin/healthcare-moderation/ai-auto-moderate-batch?country=${country}&limit=50`
      );
      
      if (response.data.success) {
        const { results } = response.data;
        toast({
          title: 'Batch Moderation Complete',
          description: `Approved: ${results.approved}, Rejected: ${results.rejected}, Manual Review: ${results.manual_review}`,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Batch moderation failed',
        variant: 'destructive',
      });
    } finally {
      setRunningBatch(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Moderation Settings - {country}
          </CardTitle>
          <CardDescription>
            Configure moderation behavior for this country
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Master Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div>
              <p className="font-medium">Enable Moderation</p>
              <p className="text-sm text-gray-500">Master switch for the moderation system</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {/* AI Auto-Approval */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  AI Auto-Approval
                </p>
                <p className="text-sm text-gray-500">Automatically approve low-risk jobs</p>
              </div>
              <Switch
                checked={settings.ai_auto_approval_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_auto_approval_enabled: checked }))}
              />
            </div>
            
            {settings.ai_auto_approval_enabled && (
              <div className="pl-6 space-y-2">
                <Label>Auto-approve if fraud score ≤</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.ai_auto_approval_threshold}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      ai_auto_approval_threshold: parseInt(e.target.value) || 0 
                    }))}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">
                    (Lower = stricter, only very safe jobs auto-approved)
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* AI Auto-Reject */}
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  AI Auto-Reject
                </p>
                <p className="text-sm text-gray-500">Automatically reject high-risk jobs</p>
              </div>
              <Switch
                checked={settings.ai_auto_reject_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, ai_auto_reject_enabled: checked }))}
              />
            </div>
            
            {settings.ai_auto_reject_enabled && (
              <div className="pl-6 space-y-2">
                <Label>Auto-reject if fraud score ≥</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={settings.ai_auto_reject_threshold}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      ai_auto_reject_threshold: parseInt(e.target.value) || 0 
                    }))}
                    className="w-24"
                  />
                  <span className="text-sm text-gray-500">
                    (Higher = stricter, only very risky jobs auto-rejected)
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Other Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Compliance Checks</p>
                <p className="text-xs text-gray-500">Run compliance analysis</p>
              </div>
              <Switch
                checked={settings.compliance_checks_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, compliance_checks_enabled: checked }))}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Email Notifications</p>
                <p className="text-xs text-gray-500">Notify employers on decisions</p>
              </div>
              <Switch
                checked={settings.email_notifications_enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, email_notifications_enabled: checked }))}
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={fetchSettings}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch AI Moderation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Batch AI Moderation
          </CardTitle>
          <CardDescription>
            Run AI auto-moderation on pending jobs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div>
              <p className="font-medium">Process Pending Queue</p>
              <p className="text-sm text-gray-600">
                Run AI moderation on up to 50 pending jobs in {country}
              </p>
            </div>
            <Button 
              onClick={runBatchModeration} 
              disabled={runningBatch || !settings.enabled}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {runningBatch ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Run Batch Moderation
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Category Rules Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clipboard className="h-5 w-5" />
            Category Compliance Rules - {country}
          </CardTitle>
          <CardDescription>
            View and manage compliance rules per job category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(categoryRules).map(([category, rules]) => (
              <div
                key={category}
                className="p-4 border rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
                onClick={() => setSelectedCategory(category)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{rules.icon}</span>
                  <span className="font-medium">{rules.name}</span>
                </div>
                <div className="text-sm text-gray-500 space-y-1">
                  <p>Pricing: <Badge variant="outline">{rules.pricingBand}</Badge></p>
                  <p>Checks: {rules.complianceChecksCount}</p>
                  <p>Flags: {rules.flagTypesCount}</p>
                  <p>Background: {rules.backgroundCheckType}</p>
                  {rules.safeguardingEnabled && (
                    <Badge className="bg-purple-100 text-purple-700">Safeguarding</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Audit Log Tab
const AuditLogTab = ({ country }) => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchLogs();
  }, [country]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/healthcare-moderation/audit-log`);
      if (response.data.success) {
        setLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching audit log:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit Log</CardTitle>
        <CardDescription>Recent moderation actions</CardDescription>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No audit log entries</p>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">{log.action}</Badge>
                    {log.job_id && <span className="text-sm text-gray-500">Job: {log.job_id}</span>}
                  </div>
                  {log.reason && <p className="text-sm text-gray-600 mt-1">{log.reason}</p>}
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(log.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminHealthcareModeration;
